/**
 * Fault text, in the student's language.
 *
 * A catalogue that nothing reads is not a translation. `tool.etch.fault.*` had
 * been written out in full in all four catalogues - labels, complaints, truths,
 * evidence bullets - and every one of those strings was dead: the shift report
 * rendered `fault.complaint` straight off the data object, so a student reading
 * in Chinese got the English. This file is the missing half.
 *
 * ENGLISH STAYS IN THE .ts, and that is deliberate rather than a transition
 * state. A fault is a piece of teaching written next to the physics it
 * describes - the complaint, the truth and the evidence only make sense beside
 * the `apply()` that produces them, and moving them into a catalogue would put
 * three files between a lesson and the thing it teaches. So the data carries
 * the source of record and the catalogue carries the translations, joined by
 * `tOr`: present a key and it wins, miss and the English is already there.
 *
 * That also makes a partial translation a working one. A language with half
 * its faults done shows half in that language and half in English, rather than
 * failing or blanking - which is what lets this be finished tool by tool.
 *
 * KEYS COME FROM IDS, never from array position. `tool.dicing.fault.glazedWheel`
 * survives someone reordering the fault list; `tool.dicing.fault.3` would
 * silently re-point every translation at the wrong fault.
 */

import { t, tList, tOr } from '../../i18n/i18n'
import type { ToolAspect, ToolFault, ToolReading } from './model'

/** A list from the catalogue, or the English the data already carries. */
function tListOr(key: string, fallback: string[]): string[] {
  const found = tList(key)
  return found.length ? found : fallback
}

/** The tool's own name, which is prose and so is itself translated. */
export const toolLabelOf = (model: { toolId: string; toolLabel: string }): string =>
  tOr(`tool.${model.toolId}.ToolLabel`, model.toolLabel)

/**
 * One fault, translated. Returns a plain object rather than mutating so the
 * catalogue can be re-read after a language change without the underlying
 * model having been touched.
 */
export function localiseFault<A extends string, C>(
  toolId: string,
  fault: ToolFault<A, C>,
): { label: string; complaint: string; truth: string; evidence: string[] } {
  const base = `tool.${toolId}.fault.${fault.id}`
  return {
    label: tOr(`${base}.Label`, fault.label),
    complaint: tOr(`${base}.Complaint`, fault.complaint),
    truth: tOr(`${base}.Truth`, fault.truth),
    evidence: tListOr(`${base}.Evidence`, fault.evidence),
  }
}

/**
 * One repairable aspect: what it is called, and what fixing it involves.
 *
 * Takes the id and the two fallbacks loose rather than a `ToolAspect`, because
 * the same aspect reaches this from two shapes - the catalogue of aspects,
 * which calls the id `id`, and a diagnosis trial, which calls it `aspect`.
 * Asking for a `ToolAspect` would mean the trial site had to build a fake one
 * with a downtime it does not have.
 */
export function localiseAspect(
  toolId: string,
  id: string,
  label: string,
  action: string,
): { label: string; action: string } {
  const base = `tool.${toolId}.aspect.${id}`
  return { label: tOr(`${base}.Label`, label), action: tOr(`${base}.Action`, action) }
}

/** The same, for a `ToolAspect` straight out of a model. */
export const localiseToolAspect = <A extends string>(toolId: string, aspect: ToolAspect<A>) =>
  localiseAspect(toolId, aspect.id, aspect.label, aspect.action)

/**
 * One bench reading.
 *
 * Only the label and the limit are translated. The VALUE is a measurement the
 * solver produced - "42 mTorr/min" - and putting a number through a catalogue
 * would be an invitation to translate the number.
 */
export function localiseReading(toolId: string, reading: ToolReading): ToolReading {
  const base = `tool.${toolId}.reading.${reading.id}`
  return {
    ...reading,
    label: tOr(`${base}.Label`, reading.label),
    limit: reading.limit ? tOr(`${base}.Limit`, reading.limit) : reading.limit,
  }
}

/** Every reading of a list, translated in place. */
export const localiseReadings = (toolId: string, readings: ToolReading[]): ToolReading[] =>
  readings.map((r) => localiseReading(toolId, r))

/** Re-exported so a call site needs one import rather than two. */
export { t }
